import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import { syncGateReason } from "../lib/gate_reason"
import { showConfirmModal } from "../lib/custom_confirm"
import { setButtonLoading } from "../lib/button_loading"

// Carrier Approval, the Trust Center wizard's step 5 (#18783, Phase 3b of
// #18673). UX only — every decision is the server's:
//
// - Send My Fix (brand): the Ready Gate (DESIGN.md PATTERN-3). Grey until a
//   flagged field differs from what the carriers rejected AND every open field
//   passes the wizard's own format checks (asked of the trust-center-wizard
//   outlet, so there is one copy of them), with the line naming what is left.
//   TrustCenter::AgentBrandFix re-checks all of it.
// - Send My Fix (campaign): the server's hold reason greys it; applying a
//   template or saving a screenshot re-opens it, and the server re-checks.
// - Send the Text Again, Try Again, Add a Note to the Ticket.
//
// Busy buttons keep their label (BUTTON-8): disabled + aria-busy + spinner.
const PHONE_ATTRS = ["mobile_phone", "auth_rep_phone"]
const EIN_ATTR = "business_registration_number"
const KIT_EVENTS = ["trust-center-campaign-kit:templateApplied", "trust-center-campaign-kit:evidenceSaved"]
const ADDRESS_EVENT = "trust-center-wizard:addressChecked"
const ADDRESS_BLOCK = "[data-trust-center-wizard-target='addressBlock']"
const VERIFY_CELL = "Verify your cell"

// The value as the server compares it (TrustCenter::BrandFixForm.comparable):
// phones and the EIN by their digits, everything else trimmed.
export function comparable(attr, value) {
  const text = String(value ?? "").trim()
  if (PHONE_ATTRS.includes(attr)) return text.replace(/\D/g, "").slice(-10)
  if (attr === EIN_ATTR) return text.replace(/\D/g, "").slice(0, 9)
  return text
}

// "Change A first" / "Change A or B first" / "Change A, B, or C first".
export function changeFirst(labels) {
  if (labels.length <= 1) return `Change ${labels[0] || "a flagged field"} first`
  const last = labels[labels.length - 1]
  const joiner = labels.length > 2 ? ", or " : " or "
  return `Change ${labels.slice(0, -1).join(", ")}${joiner}${last} first`
}

export default class extends Controller {
  static targets = [
    "message", "yesButton",
    "fixForm", "fixField", "flaggedField", "sameHint", "fixHint", "fixButton",
    "campaignHint", "campaignButton",
    "noteForm", "noteInput", "noteSubmit",
  ]

  static outlets = ["trust-center-wizard"]

  static values = {
    sendMyFixUrl: String,
    sendYesTextUrl: String,
    ticketNoteUrl: String,
    submitBrandUrl: String,
    submitCampaignUrl: String,
    resubmitCampaignUrl: String,
  }

  connect() {
    this.onEdit = (event) => {
      // The wizard skips edits in a no-dirty area, so a fix card's country
      // change re-syncs its State / Province list here (#19798).
      if (event?.type === "change" && this.hasTrustCenterWizardOutlet) {
        this.trustCenterWizardOutlet.syncRegionOptions(event.target)
      }
      this.scheduleAddressCheck(event?.target)
      this.refreshFixGate()
    }
    this.onAddressChecked = () => this.refreshFixGate()
    this.onKitSaved = (event) => this.applyCampaignGate(event?.detail?.resubmit)
    this.element.addEventListener("input", this.onEdit)
    this.element.addEventListener("change", this.onEdit)
    this.element.addEventListener("trust-center-otp:verified", this.onEdit)
    KIT_EVENTS.forEach((name) => this.element.addEventListener(name, this.onKitSaved))
    this.element.addEventListener(ADDRESS_EVENT, this.onAddressChecked)
    this.refreshFixGate()
  }

  disconnect() {
    this.element.removeEventListener("input", this.onEdit)
    this.element.removeEventListener("change", this.onEdit)
    this.element.removeEventListener("trust-center-otp:verified", this.onEdit)
    KIT_EVENTS.forEach((name) => this.element.removeEventListener(name, this.onKitSaved))
    this.element.removeEventListener(ADDRESS_EVENT, this.onAddressChecked)
  }

  // The wizard's checks become available once its outlet connects; a flagged
  // address is checked with USPS straight away (PATTERN-16).
  trustCenterWizardOutletConnected() {
    const block = this.addressBlock()
    if (block) this.trustCenterWizardOutlet.checkAddress(block)
    this.refreshFixGate()
  }

  // ----- USPS Check (the wizard's, on the fix form's address block) ----------

  addressBlock() {
    return this.hasFixFormTarget && this.fixFormTarget.matches(ADDRESS_BLOCK) ? this.fixFormTarget : null
  }

  // The wizard skips edits in a no-dirty area, so its USPS timer is started here.
  scheduleAddressCheck(field) {
    const block = this.addressBlock()
    if (!block || !this.hasTrustCenterWizardOutlet || !(field instanceof Element) || !block.contains(field)) return
    this.trustCenterWizardOutlet.scheduleAddressCheck(field)
  }

  // "USPS Address Check" while it runs, "Fix the Address USPS Can't Find" when refused.
  addressItem() {
    const block = this.addressBlock()
    if (!block || !this.hasTrustCenterWizardOutlet) return null
    return this.trustCenterWizardOutlet.addressGateItem(block)
  }

  // ----- Send My Fix (brand) ---------------------------------------------

  refreshFixGate() {
    if (!this.hasFixButtonTarget || !this.hasFixFormTarget) return []

    let changed = false
    const problems = []
    this.fixFieldTargets.forEach((field) => {
      const same = comparable(field.dataset.fixAttr, field.value) ===
        comparable(field.dataset.fixAttr, field.dataset.rejectedValue)
      if (!same) changed = true
      this.paintSame(field, same)
      const problem = this.problemFor(field)
      if (problem) problems.push(problem)
    })
    const address = this.addressItem()
    if (address) problems.push(address)
    const items = changed ? problems : [changeFirst(this.fixFieldTargets.map((f) => f.dataset.gateLabel)), ...problems]
    const reason = items.join(" · ")
    if (this.hasFixHintTarget) this.fixHintTarget.textContent = reason
    syncGateReason(this.fixButtonTarget, { ready: items.length === 0, reason })
    return items
  }

  paintSame(field, same) {
    const wrap = this.flaggedFieldTargets.find((el) => el.contains(field))
    if (!wrap) return
    wrap.classList.toggle("gl-flagged-field--changed", !same)
    const hint = this.sameHintTargets.find((el) => wrap.contains(el))
    if (hint) hint.hidden = !same
  }

  // What is left for one field, as the gate line names it (null when ready).
  problemFor(field) {
    const label = field.dataset.gateLabel
    if (!field.value.trim()) return field.hasAttribute("data-wizard-optional") ? null : `Enter ${label}`
    if (field.dataset.fixAttr === "mobile_phone" && !this.cellVerified(field)) return VERIFY_CELL
    if (!this.hasTrustCenterWizardOutlet) return null
    return this.trustCenterWizardOutlet.fieldProblem(field) ? `Fix ${label}` : null
  }

  // A new cell counts only once the server verified that exact number.
  cellVerified(field) {
    const otp = field.closest("[data-controller~='trust-center-otp']")
    const verified = otp?.dataset?.verifiedPhone || ""
    return verified !== "" && comparable("mobile_phone", verified) === comparable("mobile_phone", field.value)
  }

  async sendMyFix(event) {
    event.preventDefault()
    const button = this.fixButtonTarget
    if (button.disabled || button.getAttribute("aria-busy") === "true") return
    if (this.hasFixFormTarget && this.refreshFixGate().length) return
    if (!(await showConfirmModal(button.dataset.confirmMessage || "Send your fix to the carriers?"))) return

    const fix = Object.fromEntries(this.fixFieldTargets.map((field) => [field.dataset.fixAttr, field.value]))
    const data = await this.postJson(this.sendMyFixUrlValue, { fix }, button)
    if (!data) return this.refreshFixGate()
    if (data.success) {
      this.showMessage(data.message, "success")
      return this.reload()
    }
    this.showMessage(data.message, data.held ? "info" : "danger")
    this.markFieldErrors(data.field_errors || {})
    this.refreshFixGate()
  }

  markFieldErrors(errors) {
    Object.entries(errors).forEach(([attr, messages]) => {
      const field = this.fixFieldTargets.find((el) => el.dataset.fixAttr === attr)
      if (!field) return
      const message = [].concat(messages).join(" ")
      if (this.hasTrustCenterWizardOutlet) this.trustCenterWizardOutlet.markInvalid(field, message)
      else field.classList.add("is-invalid")
    })
  }

  // ----- Send My Fix (campaign) ------------------------------------------

  // The server's recomputed gate after Apply Template / a saved screenshot
  // (#14720). A brand or attempt hold stays closed with its own reason; with
  // no answer the button keeps what the page rendered.
  applyCampaignGate(gate) {
    if (!this.hasCampaignButtonTarget || !gate) return
    const reason = gate.ready ? "" : gate.reason || ""
    if (this.hasCampaignHintTarget) this.campaignHintTarget.textContent = reason
    syncGateReason(this.campaignButtonTarget, { ready: Boolean(gate.ready), reason })
  }

  async sendCampaignFix(event) {
    event.preventDefault()
    const button = event.currentTarget
    if (button.disabled) return
    const data = await this.postJson(this.resubmitCampaignUrlValue, {}, button)
    if (!data) return
    if (data.success) {
      this.showMessage(data.message, "success")
      return this.reload()
    }
    this.showMessage(data.message, data.held ? "info" : "danger")
  }

  // ----- One-Thing Banner, Try Again -----------------------------------------

  async sendYesText(event) {
    event.preventDefault()
    const data = await this.postJson(this.sendYesTextUrlValue, {}, event.currentTarget)
    if (data) this.showMessage(data.message, data.success ? "success" : "danger")
  }

  retryBrand(event) {
    return this.retry(event, this.submitBrandUrlValue)
  }

  retryCampaign(event) {
    return this.retry(event, this.submitCampaignUrlValue)
  }

  async retry(event, url) {
    event.preventDefault()
    const data = await this.postJson(url, {}, event.currentTarget)
    if (!data) return
    if (data.success) {
      this.showMessage(data.message, "success")
      return this.reload()
    }
    this.showMessage(data.message, data.guarded ? "info" : "danger")
  }

  // ----- Handoff Card: Add a Note to the Ticket ------------------------------

  openNote(event) {
    event.preventDefault()
    const form = this.noteFormFor(event.currentTarget.dataset.part)
    if (!form) return
    form.hidden = false
    this.noteInputTargets.find((input) => form.contains(input))?.focus()
  }

  async addNote(event) {
    event.preventDefault()
    const part = event.currentTarget.dataset.part
    const form = this.noteFormFor(part)
    const input = this.noteInputTargets.find((el) => form?.contains(el))
    if (!input) return
    if (!input.value.trim()) return this.showMessage("Write your note first.", "danger")

    const data = await this.postJson(this.ticketNoteUrlValue, { part, note: input.value }, event.currentTarget)
    if (!data) return
    this.showMessage(data.message, data.success ? "success" : "danger")
    if (data.success) {
      input.value = ""
      form.hidden = true
    }
  }

  noteFormFor(part) {
    return this.noteFormTargets.find((form) => form.dataset.part === part)
  }

  // ----- plumbing ----------------------------------------------------------

  async postJson(url, body, button) {
    if (button?.getAttribute("aria-busy") === "true") return null
    this.setBusy(button, true)
    try {
      const response = await post(url, { body: JSON.stringify(body), contentType: "application/json",
        responseKind: "json" })
      const data = await response.json
      // A gated refusal (CrmModuleGated) carries `error`, not `message` (#19798).
      if (data && !data.message && data.error) data.message = data.error
      return data
    } catch {
      this.showMessage("Something went wrong. Please try again.", "danger")
      return null
    } finally {
      this.setBusy(button, false)
    }
  }

  // BUTTON-8: the shared busy state — the spinner takes the icon's place, label kept.
  setBusy(button, busy) {
    setButtonLoading(button, busy)
  }

  showMessage(text, tone) {
    if (!this.hasMessageTarget || !text) return
    this.messageTarget.textContent = text
    this.messageTarget.className = `gl-alert gl-alert--${tone}`
  }

  // The server took the action: nothing on the page is unsaved, so the
  // wizard's "Leave site?" guard must not fire on this reload (#19798).
  reload() {
    if (this.hasTrustCenterWizardOutlet) this.trustCenterWizardOutlet.clearDirty()
    window.location.reload()
  }
}
